import {Trans} from '@lingui/macro';
import {Dictionary, pickBy} from 'lodash';
import React, {ReactNode, useMemo} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Parameter} from '../../../editors/commons/parameterModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {ClientRuleAction, RuleAction} from '../../ruleActionModel';
import {ClientRuleCondition, RuleCondition} from '../../ruleConditionModel';
import {RuleActionDefinition, RuleConditionDefinition} from '../../ruleParameterModel';
import {maybeRuleActionConditionTooltip} from '../ruleActionConditionTooltip';
import {RuleActionIcon} from '../ruleActionIcon';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {getRuleLabel} from '../ruleConditionProPill';
import {
  MultilineRuleParameterCardDefaultContentStyled,
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {RuleParameterPicker} from '../ruleParameterPicker';

/*
 * Types.
 */

interface DefaultCardProps<
  T extends ClientRuleAction | ClientRuleCondition,
  D extends RuleActionDefinition | RuleConditionDefinition,
> {
  conditionOrAction: T;
  conditionOrActionDefinition: D | undefined;
  type: T extends RuleAction ? RuleDefaultCardTypesEnum.ACTION : RuleDefaultCardTypesEnum.CONDITION;
  isDisabled?: boolean;
  errorMessage?: ReactNode;
  interactionId?: string;
  onUpdate: HandlerOf<T>;
  onDelete?: HandlerOf<T>;
}

/*
 * Component.
 */

export const RuleDefaultActionConditionCard = <
  T extends ClientRuleAction | ClientRuleCondition,
  D extends RuleActionDefinition | RuleConditionDefinition,
>(
  props: DefaultCardProps<T, D>,
) => {
  const {
    conditionOrAction,
    conditionOrActionDefinition,
    type,
    isDisabled,
    errorMessage,
    interactionId,
    onUpdate,
    onDelete,
  } = props;

  const onCancel = useMemo(
    () => (onDelete ? () => onDelete(conditionOrAction) : undefined),
    [onDelete, conditionOrAction],
  );

  const icon =
    type === RuleDefaultCardTypesEnum.CONDITION ? (
      <RuleConditionIcon id={(conditionOrAction as RuleCondition).type} />
    ) : (
      <RuleActionIcon id={(conditionOrAction as RuleAction).type} />
    );

  // This should not happen, but safety check.
  const errorLabel = (
    <Trans id="definition-not-found">
      {type} {conditionOrAction.type} not found
    </Trans>
  );
  if (!conditionOrActionDefinition) {
    return <RuleMultilineParameterCard icon={icon} title={errorLabel} onCancel={onCancel} />;
  }

  // Remove PRO/PRIME/GROWTH mention.
  const {label} = getRuleLabel(conditionOrActionDefinition.name);

  const publicParameters = pickBy(conditionOrActionDefinition.parameters, Parameter.isPublic);

  const {parameters, collapsableParameters} = renderGenericRuleParameters(
    conditionOrAction,
    conditionOrActionDefinition,
    type,
    onUpdate,
    publicParameters,
    isDisabled,
  );

  const [titleParameter, otherParameters] = partitionParameters(parameters, publicParameters);

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>{label}</MultilineRuleParameterCardDefaultTitleStyled>
      {maybeRuleActionConditionTooltip(conditionOrActionDefinition)}
      {titleParameter}
    </>
  );

  return (
    <RuleMultilineParameterCard
      interactionId={interactionId}
      icon={icon}
      onCancel={onCancel}
      title={title}
      collapsableParameters={collapsableParameters}
      errorMessage={errorMessage}
    >
      {otherParameters.length > 0 && (
        <MultilineRuleParameterCardDefaultContentStyled>
          {otherParameters}
        </MultilineRuleParameterCardDefaultContentStyled>
      )}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

/**
 * Input : The state of a condition/action
 * and its parameters specifications as described in the definition.
 *
 * Output : Render of the appropriate pickers for those parameters.
 */
export function renderGenericRuleParameters<
  T extends RuleAction | RuleCondition,
  D extends RuleActionDefinition | RuleConditionDefinition,
>(
  conditionOrAction: T,
  conditionOrActionDefinition: D,
  cardType: RuleDefaultCardTypesEnum,
  onUpdate: HandlerOf<T>,
  definitionPublicParams?: Record<string, Parameter>,
  isDisabled?: boolean,
) {
  /**
   * Schema Ref parameters can have some collapsable properties, which are listed in an array
   * instead of being a boolean.
   */
  const hasPartiallyCollapsableProps = (value: Parameter) => Array.isArray(value.collapsable);

  /**
   * Filter parameters based on whether they are collapsable or have collapsable properties. If a parameter
   * has properties that are collapsable, it should be rendered in both cases to be able to update the collapsable
   * and non-collapsable properties.
   */
  const params = pickBy(
    definitionPublicParams,
    (value: Parameter) =>
      !isDefined(value.collapsable) || hasPartiallyCollapsableProps(value) || !value.collapsable,
  );

  const collapsableParams = pickBy(
    definitionPublicParams,
    (value: Parameter) =>
      isDefined(value.collapsable) && (hasPartiallyCollapsableProps(value) || value.collapsable),
  );

  return {
    parameters: renderParameters(
      params,
      conditionOrAction,
      conditionOrActionDefinition,
      cardType,
      onUpdate,
      isDisabled,
      false,
    ),
    collapsableParameters: renderParameters(
      collapsableParams,
      conditionOrAction,
      conditionOrActionDefinition,
      cardType,
      onUpdate,
      isDisabled,
      true,
    ),
  };
}

function renderParameters<
  T extends RuleAction | RuleCondition,
  D extends RuleActionDefinition | RuleConditionDefinition,
>(
  params: Dictionary<Parameter>,
  conditionOrAction: T,
  conditionOrActionDefinition: D,
  cardType: RuleDefaultCardTypesEnum,
  onUpdate: HandlerOf<T>,
  isDisabled?: boolean,
  collapsable: boolean = false,
) {
  return Object.entries(params)
    .map(([parameterName, parameter]) => {
      if (!parameter) {
        return undefined;
      }

      const resolvedParameter = conditionOrActionDefinition.resolvedParameters[parameterName];

      return (
        <RuleParameterPicker
          key={parameter.id || parameterName}
          cardType={cardType}
          parameter={parameter}
          resolvedParameter={resolvedParameter}
          parameterName={parameterName}
          conditionOrAction={conditionOrAction}
          onUpdate={onUpdate}
          isDisabled={isDisabled}
          id={conditionOrActionDefinition.id}
          addPartiallyCollapsable={collapsable}
        />
      );
    })
    .filter(isDefined);
}

function partitionParameters(
  parameters: ReactNode[],
  manifestParams: Dictionary<Parameter>,
): [ReactNode | undefined, ReactNode[]] {
  const firstParam = Object.values(manifestParams)[0];

  // Render text inputs on their own line so you have more room.
  if (firstParam && Parameter.isString(firstParam)) {
    return [undefined, parameters];
  }

  // Otherwise, render first param alongside title.
  return [parameters[0], parameters.slice(1)];
}
